import React, { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

import { useSelector } from 'react-redux';

import { collection } from 'firebase/firestore';
import { isEqual } from 'lodash';
import moment from 'moment';

import { FSCollections } from '@src/constants/database';
import { auth, db } from '@src/firebase/firebase';
import { useFirestoreQuery } from '@src/packages/utils';
import WorkJourneyService from '@src/services/WorkJourneyService';

import { WORK_JOURNEY_PLACE_TYPE } from '../constants';
import type {
  WorkJourneyAddress,
  WorkJourneyAssignedUser,
  WorkJourneyContextValue,
  WorkJourneyForm,
  WorkJourneyListData,
  WorkJourneyMembersDrawerJourney,
  WorkJourneyModalKey,
  WorkJourneyModalVisibility,
  WorkJourneyWorkShift,
} from '../types';

const workJourneyService = new WorkJourneyService();

export const WorkJourneyContext = createContext<WorkJourneyContextValue | undefined>(undefined);

interface WorkJourneyProviderProps {
  children: ReactNode;
}

interface OrganizationsState {
  organizations: {
    organization: {
      id: string;
      divisionId: string;
    };
  };
}

/**
 * Provider to share data between WorkJourney components
 */
export const WorkJourneyProvider = ({ children }: WorkJourneyProviderProps) => {
  const orgId = useSelector(
    ({ organizations }: OrganizationsState) => organizations.organization.id,
  );
  const divId = useSelector(
    ({ organizations }: OrganizationsState) => organizations.organization.divisionId,
  );
  const { WORK_SHIFTS, ORGANIZATIONS, ADDRESSES } = FSCollections;

  const shiftQuery = collection(db, ORGANIZATIONS, orgId, WORK_SHIFTS);
  const queryResult = useFirestoreQuery(shiftQuery, [orgId]);
  const { data: workShift = [], loading: workShiftLoading } = queryResult as {
    data?: WorkJourneyWorkShift[];
    loading: boolean;
  };

  const addressQuery = collection(db, ORGANIZATIONS, orgId, ADDRESSES);
  const { data: address = [] } = useFirestoreQuery(addressQuery, [orgId]) as {
    data?: WorkJourneyAddress[];
  };
  const [refreshTrigger, setRefreshTrigger] = useState(1);

  const [workJourneyData, setWorkJourneyData] = useState<WorkJourneyListData>({
    selectedWorkJourney: null,
    workJourneyList: [],
    filters: {},
    loading: false,
  });

  const [isVisibleUsersDrawer, setIsVisibleUsersDrawer] = useState(false);
  const [selectedDrawerUsers, setSelectedDrawerUsers] = useState<WorkJourneyAssignedUser[]>([]);
  const [selectedWorkJourneyId, setSelectedWorkJourneyId] = useState<string | null>(null);
  const [selectedMembersDrawerJourney, setSelectedMembersDrawerJourney] =
    useState<WorkJourneyMembersDrawerJourney | null>(null);
  const [selectedWorkJourney, setSelectedWorkJourney] = useState<WorkJourneyForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [workJourneyType, setWorkJourneyType] = useState<string | null>(null);
  const [workJourneyForm, setWorkJourneyForm] = useState<WorkJourneyForm>({
    organizationId: orgId,
    type: null,
    workShift: null,
    shiftStartDate: moment().startOf('day'),
    shiftEndDate: null,
    workCircumstances: 'DEPENDS_ON_TIME_BAND',
    requiredMembers: 0,
    name: '',
    state: 'active',
    toleranceTime: 20,
    earlyEndTime: 20,
    breakMarkingType: 'manual',
    breakStartTime: null,
    breakEndTime: null,
    locationType: WORK_JOURNEY_PLACE_TYPE.PRESENTIAL,
    address: null,
    members: [],
    supervisors: [],
    uid: auth.currentUser?.uid ?? '',
  });

  const [modalVisibility, setModalVisibility] = useState<WorkJourneyModalVisibility>({
    returnModal: false,
    updateModal: false,
    applyChangesModal: false,
    activateModal: false,
    deactivateModal: false,
  });

  const toggleModalVisibility = (mod: WorkJourneyModalKey) =>
    setModalVisibility(prev => ({ ...prev, [mod]: !prev[mod] }));
  const initialFormState = useRef<WorkJourneyForm | null>(null);
  const setInitialFormState = (formData: WorkJourneyForm | null) => {
    initialFormState.current = formData;
  };
  const setFilters = (filters: WorkJourneyListData['filters']) =>
    setWorkJourneyData(prev => ({ ...prev, filters }));
  const setLoading = (loading: boolean) => setWorkJourneyData(prev => ({ ...prev, loading }));

  const hasUnsavedChanges = useMemo(() => {
    if (!initialFormState.current) return false;
    return !isEqual(initialFormState.current, workJourneyForm);
  }, [workJourneyForm]);

  const syncWorkJourneyMembers = async (
    workJourneyId: string,
    newMembers: WorkJourneyAssignedUser[],
  ) => {
    try {
      return await workJourneyService.updateMembers(workJourneyId, orgId, newMembers);
    } catch (error) {
      console.error('Error syncing work journey members:', error);
      throw error;
    }
  };

  const filteredWorkShifts = useMemo(
    () => workShift.filter(shift => shift.type === workJourneyForm.type),
    [workShift, workJourneyForm.type],
  );

  const value: WorkJourneyContextValue = {
    ...workJourneyData,
    setFilters,
    setLoading,
    setWorkJourneyType,
    workJourneyType,
    workJourneyForm,
    setWorkJourneyForm,
    workShift,
    workShiftLoading,
    orgId,
    divId,
    address,
    filteredWorkShifts,
    isVisibleUsersDrawer,
    setIsVisibleUsersDrawer,
    selectedDrawerUsers,
    setSelectedDrawerUsers,
    selectedWorkJourneyId,
    setSelectedWorkJourneyId,
    selectedMembersDrawerJourney,
    setSelectedMembersDrawerJourney,
    syncWorkJourneyMembers,
    refreshTrigger,
    setRefreshTrigger,
    isSaving,
    setIsSaving,
    modalVisibility,
    toggleModalVisibility,
    hasUnsavedChanges,
    setInitialFormState,
    selectedWorkJourney,
    setSelectedWorkJourney,
  };

  return <WorkJourneyContext.Provider value={value}>{children}</WorkJourneyContext.Provider>;
};

/**
 * Custom hook to use WorkJourney context
 */
export const useWorkJourneyContext = (): WorkJourneyContextValue => {
  const context = useContext(WorkJourneyContext);
  const msg = 'useWorkJourneyContext debe ser usado dentro de un WorkJourneyProvider';
  if (!context) throw new Error(msg);
  return context;
};
